import { v7 as uuidv7 } from "uuid";

/**
 * 创建一个异步延迟函数，接收一个毫秒数参数，返回一个 Promise 对象，在指定毫秒数后 resolve。
 * @param {number} ms - 延迟的毫秒数。
 * @returns {Promise<void>} - 无特定返回值的 Promise 对象。
 * @example sleepFn(1000).then(() => console.log('延迟 1 秒后执行'));
 */
export const sleepFn = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * 将对象序列化为字符串形式。
 * @param {Object} data 对象，键为字符串，值为任意类型。
 * @returns {string} 序列化后的字符串，格式为键值对以“&”连接。
 * @example serialize({a: 1, b: '2'}) => "a=1&b=2"
 */
export const serialize = (data: { [x: string]: any }) => {
  const list: Array<string> = [];
  Object.keys(data).forEach((ele) => {
    if (data[ele] != undefined) {
      list.push(`${ele}=${data[ele]}`);
    }
  });
  return list.join("&");
};

type FileInfo = {
  fileName: string;
  fileExtension: string;
  params: Record<"size" | string, string>;
};

/** 通过url获取文件信息 */
export const parseFileInfoFromUrl = (fileUrl: string): FileInfo => {
  const lastIndex = fileUrl.lastIndexOf("/");
  const fileNameWithExtensionAndParams = fileUrl.slice(lastIndex + 1);
  const fileNameParts = fileNameWithExtensionAndParams.split("?");
  const fileName = fileNameParts[0];
  const fileExtensionAndParams = fileNameParts[1] || "";
  const fileExtensionDotIndex = fileName.indexOf(".");
  const fileExtension = fileExtensionDotIndex !== -1 ? fileName.slice(fileExtensionDotIndex + 1).toLowerCase() : "";
  const params: Record<string, string> = {};
  if (fileExtensionAndParams) {
    const paramPairs = fileExtensionAndParams.split("&");
    paramPairs.forEach((pair) => {
      const [key, value] = pair.split("=");
      if (key && value) {
        params[key] = value;
      }
    });
  }
  return {
    fileName,
    fileExtension,
    params,
  };
};

/**
 * 格式化文件大小。
 * @param {number} size 文件大小数值。
 * @returns {string} 格式化后的文件大小字符串，如"123.45 KB"等。
 * @example formatFileSize(1536) => "1.50 KB"
 */
export const formatFileSize = (size: number): string => {
  if (!size) return "0 B";
  if (size < 1024) {
    return `${size} B`;
  } else if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  } else {
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }
};

/**
 * 获取当前时区相对于 GMT 的表示。
 * @returns {string} 返回格式为 GMT+/-HH 的字符串，表示当前时区与 GMT 的偏移。
 * @example getTimeZoneWithGMT() => "GMT+08"（假设当前时区为东八区）
 */
export const getTimeZoneWithGMT = () => {
  const offset = new Date().getTimezoneOffset();
  if (offset > 0) {
    return `GMT-${offset / 60}`;
  } else {
    return `GMT+${Math.abs(offset / 60)}`;
  }
};

/** 获取url参数 */
export const getUrlParamValue = (url: string, key: string): string | null => {
  const urlObject = new URL(url);
  const searchParams = urlObject.searchParams;
  // for (const [key, value] of searchParams) {
  //   console.log(`${key}: ${value}`);
  // }
  return searchParams.get(key) ?? null;
};

/** 获取cookie参数 */
export const getCookieValue = (key: string) => {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const parts = cookie.trim().split("=");
    if (parts[0] === key) {
      return parts[1];
    }
  }
  return null; // 如果未找到对应key，返回null
};

/** 设置cookie参数
 * @param {string} key - cookie的键。
 * @param {string} value - cookie的值。
 * @param {number} [expires] - cookie的过期时间，单位为天。
 */
export const setCookieValue = (key: string, value: string, expires?: number) => {
  let cookie = `${key}=${value}`;
  if (expires) {
    const date = new Date();
    date.setTime(date.getTime() + expires * 24 * 60 * 60 * 1000);
    cookie += `;expires=${date.toUTCString()}`;
  }
  document.cookie = cookie;
};

/** 最多保留两位小数并返回number类型 */
export const toFixedNumber = (num: number, digits: number): number => {
  if (Number.isNaN(num)) {
    return 0;
  }
  return Number(num.toFixed(digits) || 0);
};

/** 对传入的数，根据传入的小数位数，四舍五入并保留指定小数位数 */
export const toRoundNumber = (num: number, decimals: number, type: "ceil" | "round" | "floor" = "round"): any => {
  if (decimals < 0) {
    return num;
  }
  const tempNum = Number(num || 0);
  if (!Number.isFinite(tempNum)) {
    return num;
  }
  if (Number.isNaN(tempNum)) {
    return 0;
  }
  const factor = Math.pow(10, decimals);
  const rounded = Math[type]((tempNum + Number.EPSILON) * factor) / factor;
  return rounded.toFixed(decimals);
};

/**
 * 下载文件
 * @param {string} url - 文件地址
 * @param {string} fileName - 文件名
 * @returns {Promise<boolean>}
 */
export const downloadFile = (url: string, fileName: string, extension?: string): Promise<any> => {
  const newExtension = extension || parseFileInfoFromUrl(url).fileExtension;
  return new Promise((resolve, reject) => {
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const objectUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = `${fileName}.${newExtension}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(objectUrl);
        document.body.removeChild(a);
        resolve(true);
      })
      .catch((error) => {
        console.error("文件下载失败:", error);
        reject(error);
      });
  });
};

/** 获取uuid */
export const getUUID = () => {
  return uuidv7().toUpperCase();
};

export const isSecondTimestamp = (timestamp: number) => {
  return /^[0-9]{10}$/.test(timestamp.toString());
};

export const isTimestamp = (timestamp: number) => {
  return /^[0-9]{13}$/.test(timestamp.toString());
};

/** 从时区A 转换为时区B的时间 */
export const convertTimeWithOffset = (dateString: any, sourceOffsetHours: number, targetOffsetHours: number) => {
  const oneHour = 60 * 60 * 1000; // 一个小时的毫秒数
  const sourceOffsetHour = sourceOffsetHours * oneHour; // 转换为分钟
  const targetOffsetHour = targetOffsetHours * oneHour; // 转换为分钟
  let modifiedDateString = dateString;
  if (isSecondTimestamp(modifiedDateString)) {
    modifiedDateString = modifiedDateString * 1000;
  }
  const localDate = new Date(modifiedDateString); // 解析输入的时间
  const __isTimestamp = isTimestamp(modifiedDateString);
  /** 如果是非时间戳，则无需处理utc偏移量 */
  const localOffsetHour = __isTimestamp ? (localDate.getTimezoneOffset() / 60) * oneHour : 0; // 获取本地时区偏移量
  const utcTimestamp = localDate.getTime() - sourceOffsetHour + localOffsetHour; // 转换为 UTC 时间
  const targetTimestamp = utcTimestamp + targetOffsetHour; // 转换为目标时区时间

  return new Date(targetTimestamp); // 返回新的 Date 实例
};

/**
 * 十六进制颜色值转换为 RGBA 格式
 * @param {string} hex - 十六进制颜色值，如 "#RRGGBB" 或 "#RRGGBBAA"
 * @param {number} [alpha=1] - 透明度值，范围为 0 到 1，默认值为 1（完全不透明）
 * @returns {string} - RGBA 格式的颜色值，如 "rgba(r, g, b, a)"
 * @example hexToRgba("#FF0000") => "rgba(255, 0, 0, 1)"
 * @example hexToRgba("#FF000080") => "rgba(255, 0, 0, 0.5)"
 */
export const hexToRgba = (hex: string, alpha = 1) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** 只保留有值的className */
export const classNames = (classList: Record<string, any>) => {
  return Object.keys(classList)
    .filter((key) => classList[key])
    .join(" ");
};

/**
 * 速度单位转换
 * @param {number} value - 要转换的数值
 * @param {'dm/s' | 'km/h'} fromUnit - 原始单位
 * @param {'dm/s' | 'km/h'} toUnit - 目标单位
 * @returns {number} 转换后的数值
 */
export const convertSpeed = (value: number, fromUnit: "dm/s" | "km/h", toUnit: "dm/s" | "km/h") => {
  // 定义转换关系
  const factors = {
    // 1 dm/s = 0.36 km/h
    "dm/s_to_km/h": (value: number) => value * 0.36,

    // 1 km/h = 1/0.36 dm/s ≈ 2.7778 dm/s
    "km/h_to_dm/s": (value: number) => value / 0.36,
  };

  // 相同单位直接返回
  if (fromUnit === toUnit) return value;

  // 执行转换
  const key: keyof typeof factors = `${fromUnit}_to_${toUnit}` as keyof typeof factors;

  if (factors[key]) {
    return toFixedNumber(factors[key](value), 2);
  } else {
    throw new Error("不支持的转换类型。支持的转换：dm/s ↔ km/h");
  }
};

/** 格式化数字，添加逗号 */
export const formatNumberWithComma = (value: number | string): string => {
  const num = Number(value || 0);
  if (isNaN(num)) return "0";

  // 判断小数位长度
  const parts = num.toString().split(".");
  let formatted: string;

  if (parts.length === 1) {
    // 整数
    formatted = num.toLocaleString();
  } else {
    const decimal = parts[1];
    if (decimal.length <= 2) {
      formatted = num.toLocaleString(undefined, {
        minimumFractionDigits: decimal.length,
        maximumFractionDigits: decimal.length,
      });
    } else {
      // 四舍五入保留两位小数
      const rounded = Number(num.toFixed(2));
      formatted = rounded.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
  }

  return formatted;
};

/** 转换秒为时间格式x'y'' */
export const convertSecondsToTime = (totalSeconds: number) => {
  // const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = toFixedNumber(totalSeconds % 60, 0);

  let result = "";

  // if (hours > 0) {
  //   result += `${hours}h`;
  // }
  if (minutes > 0) {
    result += `${minutes}'`;
  }
  result += `${seconds}"`;

  return result;
};

/** 转换数据为英制 */
export const convertToImperial = (value: number, unit: "km" | "m") => {
  const isInch = localStorage.getItem("unit") == "1";
  if (!isInch) return value ?? 0;
  switch (unit) {
    case "km":
      return (value ?? 0) * 0.6213712;
    case "m":
      return (value ?? 0) * 3.28084;
    default:
      return value ?? 0;
  }
};

/** 转换数据为英制 */
export const convertToImperialTime = (value: number, unit: "km" | "m") => {
  const isInch = localStorage.getItem("unit") == "1";
  if (!isInch) return value ?? 0;
  switch (unit) {
    case "km":
      return (value ?? 0) / 0.6213712;
    case "m":
      return (value ?? 0) / 3.28084;
    default:
      return value ?? 0;
  }
};

/** 转换单位，0为公制，1为英制 */
export const convertUnit = (unit: "km" | "m") => {
  const isInch = localStorage.getItem("unit") == "1";
  if (!isInch) return unit;
  switch (unit) {
    case "km":
      return "mi";
    case "m":
      return "ft";
    default:
      return unit;
  }
};
